<template>
  <div class="pa-4">
    <h1 class="text-h4 mb-6">{{ $t('dataChecks') }}</h1>

    <!-- Setup Card -->
    <v-card class="mb-4" elevation="2">
      <v-card-title>
        <v-icon class="mr-2" color="primary">mdi-cog</v-icon>
        {{ $t('setup') }}
      </v-card-title>
      <v-card-text>
        <!-- Top row - Original dropdowns -->
        <v-row>
          <!-- Community Selection -->
          <v-col cols="4">
            <h3 class="text-h6 mb-4">{{ $t('selectCommunity') }}</h3>
            <v-autocomplete
              bg-color="white"
              :label="$t('selectCommunity')"
              v-model="selectedCommunities"
              :items="communitiesAvailable"
              :filter-keys="['title','props.subtitle']"
              variant="outlined"
              item-value="ecn"
              item-title="name"
              return-object
              multiple
              chips
              clearable
              :hint="selectedCommunities.length ? `${selectedCommunities.length} ${$t('communitiesSelected')}` : ''"
              persistent-hint
            ></v-autocomplete>
          </v-col>

          <!-- Billing Cycle Selection -->
          <v-col cols="4">
            <h3 class="text-h6 mb-4">{{ $t('billingCycle') }}</h3>
            <v-select
              bg-color="white"
              :label="$t('billingCycle')"
              v-model="selectedBillingCycle"
              :items="billingCycleOptions"
              variant="outlined"
              item-value="value"
              item-title="text"
              clearable
              @update:model-value="onBillingCycleChange"
            ></v-select>
          </v-col>

          <!-- Date Range Controls -->
          <v-col cols="4">
            <h3 class="text-h6 mb-4">{{ $t('dateRange') }}</h3>
            <DateRangePicker
              :dateRange="pendingPickerDates"
              @update:dateRange="onDateRangePickerChange"
              :placeholder="$t('selectDateRange')"
            >
              <template #append-inner="{ closeMenu }">
                <div class="d-flex justify-end pa-2">
                  <TertiaryButton class="mr-2" :text="$t('cancel')" @click="onDatePickerCancel(closeMenu)" />
                  <PrimaryButton :text="$t('confirm')" @click="onDatePickerApply(closeMenu)" />
                </div>
              </template>
            </DateRangePicker>
          </v-col>
        </v-row>

        <!-- Bottom row - Actions, Details, and additional controls -->
        <v-row class="mt-6">
          <!-- Left column - Actions -->
          <v-col cols="4">
            <h3 class="text-h6 mb-4">{{ $t('actions') }}</h3>

            <v-btn
              color="error"
              variant="outlined"
              block
              :disabled="!selectedCommunities.length"
              @click="clearCommunityData"
              prepend-icon="mdi-account-group-outline"
              class="mb-3"
            >
              {{ $t('clearCommunityData') }}
            </v-btn>

            <TertiaryButton
              block
              :disabled="!dateRange"
              @click="clearDateRange"
              prepend-icon="mdi-calendar-remove"
              class="mb-4"
              :text="$t('clearDateRange')"
            />

            <SecondaryButton
              block
              :disabled="!canRunCheck"
              :loading="isRunningCheck"
              @click="runCompletenessCheck"
              class="mb-3"
              :text="$t('runCompletenessCheck')"
            />

            <SecondaryButton
              block
              :disabled="!canRunSanityCheck"
              :loading="isRunningSanityCheck"
              @click="runSanityCheck"
              class="mb-3"
              :text="$t('runSanityCheck')"
            />

            <SecondaryButton
              block
              :disabled="!canRunDynamicDistributionCheck"
              :loading="isRunningDynamicDistributionCheck"
              @click="runDynamicDistributionCheck"
              class="mb-3"
              :text="$t('runDynamicDistributionCheck')"
            />

            <PrimaryButton
              block
              :disabled="!canRunCombinedCheck"
              :loading="isRunningCombinedCheck"
              @click="runCombinedCheck"
              class="mt-3"
              :text="$t('runCombinedCheck')"
            />
          </v-col>

          <!-- Middle column - Selection Details -->
          <v-col cols="8">
            <h3 class="text-h6 mb-4">{{ $t('selectionDetails') }}</h3>
            <div class="selection-details">
              <div v-if="selectedCommunities.length > 0" class="mb-2">
                <strong>{{ $t('communitiesSelected') }}:</strong>
                <ul class="mt-1">
                  <li v-for="community in selectedCommunities" :key="community.ecn">
                    {{ community.name }} ({{ community.ecn }})
                  </li>
                </ul>
              </div>
              <div v-if="dateRange" class="mb-2">
                <strong>{{ $t('dateRange') }}:</strong> {{ formatDateRange(dateRange) }}
              </div>
              <div v-if="!selectedCommunities.length && !dateRange" class="text-grey-600">
                {{ $t('noSelectionMade') }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Results Card -->
    <v-card v-if="showResults" class="mb-4" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
        {{ $t('completenessReport') }}
        <v-spacer></v-spacer>
        <SecondaryButton
          v-if="reportResults && !isRunningCheck"
          size="small"
          variant="outlined"
          @click="downloadCompletenessReport"
          prepend-icon="mdi-download"
          class="mr-2"
          :text="$t('download')"
        />
        <v-btn
          icon
          size="small"
          @click="isCompletenessCardExpanded = !isCompletenessCardExpanded"
        >
          <v-icon>{{ isCompletenessCardExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-expand-transition>
        <v-card-text v-show="isCompletenessCardExpanded">
          <div v-if="isRunningCheck" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">{{ $t('analyzingData') }}</p>
          </div>

          <div v-else-if="reportResults">
            <pre class="report-text">{{ reportResults }}</pre>
          </div>
        </v-card-text>
      </v-expand-transition>
    </v-card>

    <!-- Sanity Results Card -->
    <v-card v-if="showSanityResults" class="mb-4" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="secondary">mdi-scale-balance</v-icon>
        {{ $t('sanityReport') }}
        <v-spacer></v-spacer>
        <SecondaryButton
          v-if="sanityReportResults && !isRunningSanityCheck"
          size="small"
          variant="outlined"
          @click="downloadSanityReport"
          prepend-icon="mdi-download"
          class="mr-2"
          :text="$t('download')"
        />
        <v-btn
          icon
          size="small"
          @click="isSanityCardExpanded = !isSanityCardExpanded"
        >
          <v-icon>{{ isSanityCardExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-expand-transition>
        <v-card-text v-show="isSanityCardExpanded">
          <div v-if="isRunningSanityCheck" class="text-center py-4">
            <v-progress-circular indeterminate color="secondary"></v-progress-circular>
            <p class="mt-2">{{ $t('analyzingEnergyBalance') }}</p>
          </div>

          <div v-else-if="sanityReportResults">
            <pre class="report-text">{{ sanityReportResults }}</pre>
          </div>
        </v-card-text>
      </v-expand-transition>
    </v-card>

    <!-- Combined Results Card -->
    <v-card v-if="showCombinedResults" class="mb-4" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="success">mdi-chart-multiple</v-icon>
        {{ $t('combinedAnalysisReport') }}
        <v-spacer></v-spacer>
        <SecondaryButton
          v-if="combinedReportResults && !isRunningCombinedCheck"
          size="small"
          variant="outlined"
          @click="downloadCombinedReport"
          prepend-icon="mdi-download"
          class="mr-2"
          :text="$t('download')"
        />
        <v-btn
          icon
          size="small"
          @click="isCombinedCardExpanded = !isCombinedCardExpanded"
        >
          <v-icon>{{ isCombinedCardExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-expand-transition>
        <v-card-text v-show="isCombinedCardExpanded">
          <div v-if="isRunningCombinedCheck" class="text-center py-4">
            <v-progress-circular indeterminate color="success"></v-progress-circular>
            <p class="mt-2">{{ $t('runningCombinedAnalysis') }}</p>
          </div>

          <div v-else-if="combinedReportResults">
            <pre class="report-text">{{ combinedReportResults }}</pre>
          </div>
        </v-card-text>
      </v-expand-transition>
    </v-card>

    <!-- Dynamic Distribution Results Card -->
    <v-card v-if="showDynamicDistributionResults" class="mb-4" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="info">mdi-vector-difference</v-icon>
        {{ $t('dynamicDistributionReport') }}
        <v-spacer></v-spacer>
        <SecondaryButton
          v-if="dynamicDistributionReportResults && !isRunningDynamicDistributionCheck"
          size="small"
          variant="outlined"
          @click="downloadDynamicDistributionReport"
          prepend-icon="mdi-download"
          class="mr-2"
          :text="$t('download')"
        />
        <v-btn
          icon
          size="small"
          @click="isDynamicDistributionCardExpanded = !isDynamicDistributionCardExpanded"
        >
          <v-icon>{{ isDynamicDistributionCardExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-expand-transition>
        <v-card-text v-show="isDynamicDistributionCardExpanded">
          <div v-if="isRunningDynamicDistributionCheck" class="text-center py-4">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
            <p class="mt-2">{{ $t('analyzingDynamicDistribution') }}</p>
          </div>

          <div v-else-if="dynamicDistributionReportResults">
            <pre class="report-text">{{ dynamicDistributionReportResults }}</pre>
          </div>
        </v-card-text>
      </v-expand-transition>
    </v-card>


  </div>
</template>

<script setup>
import { computed, ref, reactive, getCurrentInstance, watch } from 'vue';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { useMeteringPointMembershipsStore } from '@/stores/meteringPointMembershipsStore';
import { useI18n } from 'vue-i18n';
import { performCompletenessAnalysis, performSanityAnalysis } from '@/services/billRunService';
import { formatCompletenessReport, formatSanityReport, formatErrorReport, formatCombinedReport, formatDynamicDistributionReport } from '@/services/reportFormatter';
import { performDynamicDistributionAnalysis } from '@/services/billRunService';
import { PrimaryButton, SecondaryButton, TertiaryButton, DateRangePicker } from '@es-bill/es-bill-common-components';

/**
 * ViewBillRuns - Data Checks page component
 * Displays data validation and checking functionality for bill runs
 */

const communitiesStore = useCommunitiesStore();
const mpmStore = useMeteringPointMembershipsStore();
const { t } = useI18n();
const { appContext } = getCurrentInstance();
const $log = appContext.config.globalProperties.$log;

// Reactive state
const selectedCommunities = ref([]);
const selectedBillingCycle = ref(null);
const dateRange = ref(null); // { start: Date, end: Date }
const isRunningCheck = ref(false);
const reportResults = ref('');
const showResults = ref(false);

// Date range picker (shared component) staging state
const getArrayFromRange = (range) => {
  if (range && range.start && range.end) {
    return [range.start, range.end];
  }
  return [];
};
const pendingPickerDates = ref(getArrayFromRange(dateRange.value));

// Sanity check state
const isRunningSanityCheck = ref(false);
const sanityReportResults = ref('');
const showSanityResults = ref(false);

// Combined check state
const isRunningCombinedCheck = ref(false);
const combinedReportResults = ref('');
const showCombinedResults = ref(false);

// Dynamic distribution check state
const isRunningDynamicDistributionCheck = ref(false);
const dynamicDistributionReportResults = ref('');
const showDynamicDistributionResults = ref(false);

// Card expansion states
const isCompletenessCardExpanded = ref(true);
const isSanityCardExpanded = ref(true);
const isCombinedCardExpanded = ref(true);
const isDynamicDistributionCardExpanded = ref(true);

/**
 * Reset all report states when configuration changes
 */
const resetAllReports = () => {
  // Reset completeness report
  showResults.value = false;
  reportResults.value = '';
  isRunningCheck.value = false;

  // Reset sanity report
  showSanityResults.value = false;
  sanityReportResults.value = '';
  isRunningSanityCheck.value = false;

  // Reset combined report
  showCombinedResults.value = false;
  combinedReportResults.value = '';
  isRunningCombinedCheck.value = false;

  // Reset dynamic distribution report
  showDynamicDistributionResults.value = false;
  dynamicDistributionReportResults.value = '';
  isRunningDynamicDistributionCheck.value = false;

  // Reset expansion states to default
  isCompletenessCardExpanded.value = true;
  isSanityCardExpanded.value = true;
  isCombinedCardExpanded.value = true;
  isDynamicDistributionCardExpanded.value = true;
};

// Computed properties
const communitiesAvailable = computed(() =>
  communitiesStore.allCommunities.map(c => ({
    ...c,
    props: { subtitle: c.ecn + ' / ' + c.ecid }
  }))
);

const billingCycleOptions = computed(() => [
  { text: t('monthly'), value: 'monthly' },
  { text: t('quarterly'), value: 'quarterly' },
  { text: t('halfYear'), value: 'halfYear' },
  { text: t('yearly'), value: 'yearly' }
]);

const canRunCheck = computed(() => {
  return selectedCommunities.value.length > 0 && dateRange.value;
});

const canRunSanityCheck = computed(() => {
  return selectedCommunities.value.length > 0 && dateRange.value;
});

const canRunCombinedCheck = computed(() => {
  return selectedCommunities.value.length > 0 && dateRange.value;
});

const canRunDynamicDistributionCheck = computed(() => {
  return selectedCommunities.value.length > 0 && dateRange.value;
});

// Watchers to reset reports when configuration changes
watch(selectedCommunities, (newVal, oldVal) => {
  if (newVal.length !== oldVal.length || newVal.some((c, i) => c.ecn !== oldVal[i]?.ecn)) {
    resetAllReports();
  }
});

watch(dateRange, (newVal, oldVal) => {
  if (newVal && (!oldVal || newVal.start.getTime() !== oldVal.start.getTime() || newVal.end.getTime() !== oldVal.end.getTime())) {
    resetAllReports();
  } else if (!newVal && oldVal) {
    resetAllReports();
  }
});

watch(selectedBillingCycle, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    resetAllReports();
  }
});

// Helper functions
const getBillingCycleText = (value) => {
  const option = billingCycleOptions.value.find(o => o.value === value);
  return option ? option.text : value;
};

const formatDateRange = (range) => {
  if (!range || !range.start || !range.end) return '';
  const start = new Date(range.start).toLocaleDateString();
  const end = new Date(range.end).toLocaleDateString();
  return `${start} - ${end}`;
};

// DateRangePicker integration: handlers and helpers
const onDateRangePickerChange = (newDates) => {
  pendingPickerDates.value = newDates;
};

const onDatePickerApply = (closeMenu) => {
  if (pendingPickerDates.value && pendingPickerDates.value.length >= 2) {
    const startDate = new Date(pendingPickerDates.value[0]);
    const endDate = new Date(pendingPickerDates.value[pendingPickerDates.value.length - 1]);
    dateRange.value = { start: startDate, end: endDate };
    selectedBillingCycle.value = null; // mimic previous behavior when custom range selected
  }
  if (typeof closeMenu === 'function') closeMenu();
};

const onDatePickerCancel = (closeMenu) => {
  // Revert pending dates to currently applied dateRange
  pendingPickerDates.value = getArrayFromRange(dateRange.value);
  if (typeof closeMenu === 'function') closeMenu();
};

/**
 * Calculate the last completed period based on billing cycle
 */
const getLastCompletedPeriod = (cycle) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based

  let start, end;

  switch (cycle) {
    case 'monthly':
      // Last completed month
      if (currentMonth === 0) {
        start = new Date(currentYear - 1, 11, 1); // December of previous year
        end = new Date(currentYear - 1, 11, 31);
      } else {
        start = new Date(currentYear, currentMonth - 1, 1);
        end = new Date(currentYear, currentMonth, 0); // Last day of previous month
      }
      break;

    case 'quarterly':
      // Last completed quarter
      const currentQuarter = Math.floor(currentMonth / 3);
      if (currentQuarter === 0) {
        // Previous year Q4
        start = new Date(currentYear - 1, 9, 1); // October
        end = new Date(currentYear - 1, 11, 31); // December
      } else {
        const prevQuarterStart = (currentQuarter - 1) * 3;
        start = new Date(currentYear, prevQuarterStart, 1);
        end = new Date(currentYear, prevQuarterStart + 3, 0);
      }
      break;

    case 'halfYear':
      // Last completed half year
      if (currentMonth < 6) {
        // Previous year H2
        start = new Date(currentYear - 1, 6, 1); // July
        end = new Date(currentYear - 1, 11, 31); // December
      } else {
        // Current year H1
        start = new Date(currentYear, 0, 1); // January
        end = new Date(currentYear, 5, 30); // June
      }
      break;

    case 'yearly':
      // Last completed year
      start = new Date(currentYear - 1, 0, 1); // January 1st of previous year
      end = new Date(currentYear - 1, 11, 31); // December 31st of previous year
      break;
  }

  return { start, end };
};

/**
 * Handle billing cycle change
 */
const onBillingCycleChange = (newCycle) => {
  if (newCycle) {
    dateRange.value = getLastCompletedPeriod(newCycle);
  } else {
    dateRange.value = null;
  }
};

/**
 * Date range picker functions
 */
const confirmDateRangeSelection = () => {
  if (tempDateRange.start && tempDateRange.end) {
    dateRange.value = {
      start: new Date(tempDateRange.start),
      end: new Date(tempDateRange.end)
    };
    // Clear billing cycle selection when custom range is set
    selectedBillingCycle.value = null;
  }
  showDateRangePicker.value = false;
};

const cancelDateRangeSelection = () => {
  tempDateRange.start = null;
  tempDateRange.end = null;
  showDateRangePicker.value = false;
};

/**
 * Clear the selected date range
 */
const clearDateRange = () => {
  dateRange.value = null;
  selectedBillingCycle.value = null;
};

/**
 * Clear the selected community data
 */
const clearCommunityData = () => {
  selectedCommunities.value = [];
};

/**
 * Run the completeness check analysis
 */
const runCompletenessCheck = async () => {
  if (!canRunCheck.value) return;

  isRunningCheck.value = true;
  showResults.value = true;
  isCompletenessCardExpanded.value = true; // Expand when new results come in

  try {
    // Perform completeness analysis with memberships data
    const analysisResults = await performCompletenessAnalysis(
      selectedCommunities.value,
      dateRange.value,
      mpmStore.allMeteringPointMemberships
    );

    // Format the report using the new formatter service
    reportResults.value = formatCompletenessReport(
      analysisResults,
      selectedCommunities.value,
      dateRange.value,
      selectedBillingCycle.value,
      getBillingCycleText,
      formatDateRange
    );

  } catch (error) {
    $log.error('Error running completeness check:', error);
    reportResults.value = formatErrorReport(error);
  } finally {
    isRunningCheck.value = false;
  }
};

/**
 * Run the sanity check analysis (energy balance validation)
 */
const runSanityCheck = async () => {
  if (!canRunSanityCheck.value) return;

  isRunningSanityCheck.value = true;
  showSanityResults.value = true;
  isSanityCardExpanded.value = true; // Expand when new results come in

  try {
    // Perform sanity analysis with memberships data
    const analysisResults = await performSanityAnalysis(
      selectedCommunities.value,
      dateRange.value,
      mpmStore.allMeteringPointMemberships
    );

    // Format the report using the sanity formatter
    sanityReportResults.value = formatSanityReport(
      analysisResults,
      selectedCommunities.value,
      dateRange.value,
      selectedBillingCycle.value,
      getBillingCycleText,
      formatDateRange
    );

  } catch (error) {
    $log.error('Error running sanity check:', error);
    sanityReportResults.value = formatErrorReport(error);
  } finally {
    isRunningSanityCheck.value = false;
  }
};

/**
 * Run the combined analysis (completeness + sanity + dynamic distribution)
 */
const runCombinedCheck = async () => {
  if (!canRunCombinedCheck.value) return;

  isRunningCombinedCheck.value = true;
  showCombinedResults.value = true;
  isCombinedCardExpanded.value = true; // Expand when new results come in

  try {
    // Perform combined analysis with memberships data - all three analyses
    const analysisResults = await Promise.all([
      performCompletenessAnalysis(
        selectedCommunities.value,
        dateRange.value,
        mpmStore.allMeteringPointMemberships
      ),
      performSanityAnalysis(
        selectedCommunities.value,
        dateRange.value,
        mpmStore.allMeteringPointMemberships
      ),
      performDynamicDistributionAnalysis(
        selectedCommunities.value,
        dateRange.value,
        mpmStore.allMeteringPointMemberships
      )
    ]);

    // Format the combined report using the new formatter
    combinedReportResults.value = formatCombinedReport(
      analysisResults[0], // completeness results
      analysisResults[1], // sanity results
      selectedCommunities.value,
      dateRange.value,
      selectedBillingCycle.value,
      getBillingCycleText,
      formatDateRange,
      analysisResults[2] // dynamic distribution results
    );

  } catch (error) {
    $log.error('Error running combined check:', error);
    combinedReportResults.value = formatErrorReport(error);
  } finally {
    isRunningCombinedCheck.value = false;
  }
};

/**
 * Run the dynamic distribution analysis
 */
const runDynamicDistributionCheck = async () => {
  if (!canRunDynamicDistributionCheck.value) return;

  isRunningDynamicDistributionCheck.value = true;
  showDynamicDistributionResults.value = true;
  isDynamicDistributionCardExpanded.value = true; // Expand when new results come in

  try {
    // Perform dynamic distribution analysis with memberships data
    const analysisResults = await performDynamicDistributionAnalysis(
      selectedCommunities.value,
      dateRange.value,
      mpmStore.allMeteringPointMemberships
    );

    // Format the report using the dynamic distribution formatter
    dynamicDistributionReportResults.value = await formatDynamicDistributionReport(
      analysisResults,
      selectedCommunities.value,
      dateRange.value,
      selectedBillingCycle.value,
      getBillingCycleText,
      formatDateRange
    );

  } catch (error) {
    $log.error('Error running dynamic distribution check:', error);
    dynamicDistributionReportResults.value = formatErrorReport(error);
  } finally {
    isRunningDynamicDistributionCheck.value = false;
  }
};

/**
 * Download report as text file
 * @param {string} content - Report content
 * @param {string} filename - Filename for download
 */
const downloadReport = (content, filename) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Generate filename for report download
 * @param {string} reportType - Type of report
 * @returns {string} Generated filename
 */
const generateFilename = (reportType) => {
  // Report generation date in YYYYMMDD format
  const reportDate = new Date();
  const dateStr = [
    reportDate.getFullYear(),
    (reportDate.getMonth() + 1).toString().padStart(2, '0'),
    reportDate.getDate().toString().padStart(2, '0')
  ].join('');

  // Handle single vs multiple communities
  const communities = selectedCommunities.value.length === 1
    ? selectedCommunities.value[0].ecn
    : 'MultipleCommunities';

  return `${dateStr}-${reportType}-${communities}.txt`;
};



/**
 * Download completeness report
 */
const downloadCompletenessReport = () => {
  if (!reportResults.value) return;
  const filename = generateFilename('completeness-report');
  downloadReport(reportResults.value, filename);
};

/**
 * Download sanity report
 */
const downloadSanityReport = () => {
  if (!sanityReportResults.value) return;
  const filename = generateFilename('sanity-report');
  downloadReport(sanityReportResults.value, filename);
};

/**
 * Download combined report
 */
const downloadCombinedReport = () => {
  if (!combinedReportResults.value) return;
  const filename = generateFilename('combined-report');
  downloadReport(combinedReportResults.value, filename);
};

/**
 * Download dynamic distribution report
 */
const downloadDynamicDistributionReport = () => {
  if (!dynamicDistributionReportResults.value) return;
  const filename = generateFilename('dynamic-distribution-report');
  downloadReport(dynamicDistributionReportResults.value, filename);
};

// Metadata is preloaded in App.vue via AllDataService
</script>

<style scoped>
.report-text {
  font-family: 'Courier New', monospace;
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  white-space: pre-wrap;
  overflow-x: auto;
  font-size: 14px;
  line-height: 1.4;
}
</style>
